import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Measurement } from '@core/interfaces/measurement';
import { SurveyIdOffline } from '@core/interfaces/offline/survey-id-offline';
import { SurveyMeasurement } from '@core/interfaces/survey-measurement';
import { SurveyPoint } from '@core/interfaces/survey-point';
import { DatabaseService } from '@core/services/database.service';
import { UrlService } from '@core/services/url.service';
import { UuidService } from '@core/services/uuid.service';
import { convertPropertyToBoolean } from '@orm-entities/entity-property-converter';
import { SurveyImage } from '@orm-entities/survey-image';
import { SurveyPoints } from '@orm-entities/survey-points';
import { Observable, forkJoin, map, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class InteractiveIsometricImageDataFetcher {
  readonly #httpClient = inject(HttpClient);
  readonly #urlService = inject(UrlService);
  readonly #databaseService = inject(DatabaseService);
  readonly #uuidService = inject(UuidService);

  public fetch(fileId: string): Observable<Measurement> {
    const url = this.#urlService.getUrl(`files/${fileId}/measurements`);
    return this.#httpClient.get<Measurement>(url);
  }

  public load(fileId: string): Observable<Measurement> {
    return forkJoin({
      image: this.#databaseService.select(SurveyImage, { fileId }).pipe(map(images => images[0])),
      cmlRows: this.#databaseService.select(SurveyPoints, { fileId }),
      measurementRows: this.#loadMeasurementRowsAndAlias(fileId),
    }).pipe(map(({ image, cmlRows, measurementRows }) => this.#assemble(image, cmlRows, measurementRows)));
  }

  #assemble(
    image: SurveyImage,
    cmlRows: SurveyPoints[],
    { measurementRows, columnsHavingAlias }: { measurementRows: SurveyIdOffline[]; columnsHavingAlias: Record<string, string> },
  ): Measurement {
    const cmls: SurveyPoint[] = [];
    let measurements: SurveyMeasurement[];

    if (measurementRows.length > 0) {
      measurements = measurementRows.map(
        measurementRow =>
          ({
            ...measurementRow,
            ...Object.entries(columnsHavingAlias).reduce((acc, [column, alias]) => {
              acc[column] = measurementRow[alias];
              return acc;
            }, {}),
            measurement: Number(measurementRow.measurement),
            previousMeasurement: Number(measurementRow.previousMeasurement),
            measurementPointId: measurementRow.measurementPointId ?? this.#uuidService.generate(),
            isSkipped: convertPropertyToBoolean(measurementRow.isSkipped),
            isCustom: convertPropertyToBoolean(measurementRow.isCustom),
          }) as unknown as SurveyMeasurement,
      );
    }

    if (cmlRows.length > 0) {
      for (const cmlRow of cmlRows) {
        cmls.push({
          id: cmlRow.id,
          label: cmlRow.label,
          measurementStatus: cmlRow.measurementStatus,
          location: {
            left: Number(cmlRow.left),
            top: Number(cmlRow.top),
            height: cmlRow.height,
            width: cmlRow.width,
          },
          ids: measurements.filter(item => item.label === cmlRow.label),
          isCustom: convertPropertyToBoolean(cmlRow.isCustom),
        });
      }
    }

    return {
      image: {
        height: Number(image.height),
        width: Number(image.width),
      },
      points: cmls,
      visualEvents: [],
      signature: [],
    };
  }

  #loadMeasurementRowsAndAlias(
    fileId: string,
  ): Observable<{ measurementRows: SurveyIdOffline[]; columnsHavingAlias: Record<string, string> }> {
    return this.#databaseService.executeSql(`PRAGMA table_info('SurveyIds')`, []).pipe(
      switchMap((tableInfo: { name: string }[]) => {
        const columns = tableInfo.map(column => column.name);
        const { query, columnsHavingAlias } = this.#buildLoadSurveyIdsQuery(columns);
        return this.#databaseService.executeSql(query, [fileId]).pipe(map(measurementRows => ({ measurementRows, columnsHavingAlias })));
      }),
    );
  }

  #buildLoadSurveyIdsQuery(columns: string[]): { query: string; columnsHavingAlias: Record<string, string> } {
    const quote = (id: string): string => `"${id.replace(/"/g, '""')}"`;
    const columnsHavingAlias = columns
      .filter(column => column.includes('.'))
      .reduce(
        (acc, column) => {
          acc[column] = column.replace('.', '_');
          return acc;
        },
        {} as Record<string, string>,
      );
    const queryColumns = columns
      .map(column => {
        if (columnsHavingAlias[column]) {
          return `${quote(column)} AS ${quote(columnsHavingAlias[column])}`;
        }
        return quote(column);
      })
      .join(', ');
    return { query: `SELECT ${queryColumns} FROM ${quote('SurveyIds')} WHERE ${quote('fileId')} = ?`, columnsHavingAlias };
  }
}
